import React, { useCallback } from 'react';
import { formatTranslation, complianceOptionLabelKey } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import { Box, TextField, Stack } from '@mui/material';
import { useIntl } from 'react-intl';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { MemoAnswerCardHeader } from './AnswerCardHeader';
import { getAnswerCardSx } from './answerTypeTheme';
import { AnswerPanelProps, ComplianceAnswerConfig } from './types';
import { formatFieldError } from './formatFieldError';

export function ComplianceAnswerPanel({
  answerId,
  answer,
  fieldErrors,
  onUpdate,
  onDelete,
}: AnswerPanelProps<ComplianceAnswerConfig>): React.ReactElement {
  const intl = useIntl();

  const handleUpdate = useCallback(
    (updates: Partial<ComplianceAnswerConfig>) => {
      onUpdate(answerId, updates);
    },
    [answerId, onUpdate]
  );

  const handleDelete = useCallback(() => {
    onDelete(answerId);
  }, [answerId, onDelete]);

  const handleLabelChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleUpdate({ label: event.target.value });
    },
    [handleUpdate]
  );

  const handleRequiredChange = useCallback(
    (isRequired: boolean) => {
      handleUpdate({ isRequired });
    },
    [handleUpdate]
  );

  const handleOptionLabelChange = useCallback(
    (option: ComplianceAnswerOption, label: string) => {
      const updatedLabels = answer.optionLabels.map((ol) =>
        ol.option === option ? { ...ol, label } : ol
      );
      handleUpdate({ optionLabels: updatedLabels });
    },
    [answer.optionLabels, handleUpdate]
  );

  return (
    <Box sx={getAnswerCardSx(answer.type)}>
      <MemoAnswerCardHeader
        answerType={answer.type}
        isRequired={answer.isRequired}
        onRequiredChange={handleRequiredChange}
        onDelete={handleDelete}
      />

      <TextField
        label={formatTranslation(intl, InspectionTranslationKeys.label)}
        value={answer.label}
        onChange={handleLabelChange}
        size="medium"
        fullWidth
        required
        error={Boolean(fieldErrors?.label)}
        helperText={formatFieldError(intl, fieldErrors?.label)}
        sx={{ mb: 2 }}
      />

      <Stack spacing={1.5}>
        {answer.optionLabels
          .filter((ol) => ol.option !== ComplianceAnswerOption.unset)
          .map((optionLabel) => (
            <TextField
              key={optionLabel.option}
              label={formatTranslation(
                intl,
                complianceOptionLabelKey(optionLabel.option)
              )}
              value={optionLabel.label}
              onChange={(e) =>
                handleOptionLabelChange(optionLabel.option, e.target.value)
              }
              size="medium"
              fullWidth
              error={Boolean(fieldErrors?.optionLabels?.[optionLabel.option])}
              helperText={formatFieldError(
                intl,
                fieldErrors?.optionLabels?.[optionLabel.option]
              )}
              placeholder={formatTranslation(intl, complianceOptionLabelKey(optionLabel.option))}
            />
          ))}
      </Stack>
    </Box>
  );
}

export const MemoComplianceAnswerPanel = React.memo(ComplianceAnswerPanel);
